/* eslint-disable camelcase */
/** Mock payload for Linehaul Settings → Rates & Accessorial → L Codes → carrier detail. */

import type { z } from "zod";

import type { ExpiredAccessorialHistory as ExpiredAccessorialHistoryType } from "../../hooks/react-query/settings/linehaul/useExpiredAccessorialHistory";
import {
  lCodeCarrierAddendums,
  lCodeCarrierDestinationsSchema,
  type CarrierLanesResponse,
  type LinehaulLCodesCarrierDetailContent,
} from "../../hooks/react-query/settings/linehaul/useLCodeCarrierProfile";
import type { LCodeAccessorialDetail } from "../../hooks/react-query/settings/linehaul/useLCodeEditAccessorial";

type CarrierAddendumDocument = z.infer<
  typeof lCodeCarrierAddendums.responseSchema
>["documents"][number];

type CarrierAddendumsListResponse = z.infer<
  typeof lCodeCarrierAddendums.responseSchema
>;

type LCodeCarrierDestinations = z.infer<typeof lCodeCarrierDestinationsSchema>;
type LCodeDestinationLane =
  LCodeCarrierDestinations["originSics"][number]["destinations"][number];

type MockAddendumDocumentPreview = {
  url: string;
  name: string;
  size: number;
  type: string;
  lastModified: string;
};

const mockCarrierAddendums: CarrierAddendumDocument[] = [
  {
    id: 2061,
    s3_bucket: "dylt-apex-documents-qa-documents",
    s3_key: "default/2061/latest/LaneReport.pdf",
    s3_version_id: null,
    document_type: "CARRIER_ADDENDUM",
    document_category: "linehaul",
    source: "web_portal",
    shipment_id: null,
    pro_number: null,
    trip_number: null,
    route_id: null,
    route_stop_id: null,
    carrier_id: 95128,
    original_filename: "LaneReport.pdf",
    content_type: "application/pdf",
    file_size_bytes: 229249,
    page_count: null,
    status_id: 301,
    processed_at: null,
    is_deleted: false,
    created_on: "2026-05-26T08:35:41.926877",
    created_by: "Mark Twain",
    extractions: [],
  },
  {
    id: 2062,
    s3_bucket: "dylt-apex-documents-qa-documents",
    s3_key: "default/2062/latest/RateSchedule.pdf",
    s3_version_id: null,
    document_type: "CARRIER_ADDENDUM",
    document_category: "linehaul",
    source: "web_portal",
    shipment_id: null,
    pro_number: null,
    trip_number: null,
    route_id: null,
    route_stop_id: null,
    carrier_id: 95128,
    original_filename: "RateSchedule.pdf",
    content_type: "application/pdf",
    file_size_bytes: 184320,
    page_count: null,
    status_id: 301,
    processed_at: null,
    is_deleted: false,
    created_on: "2026-05-24T14:22:10.000000",
    created_by: "Riley Brooks",
    extractions: [],
  },
  {
    id: 2063,
    s3_bucket: "dylt-apex-documents-qa-documents",
    s3_key: "default/2063/latest/AccessorialTerms.pdf",
    s3_version_id: null,
    document_type: "CARRIER_ADDENDUM",
    document_category: "linehaul",
    source: "web_portal",
    shipment_id: null,
    pro_number: null,
    trip_number: null,
    route_id: null,
    route_stop_id: null,
    carrier_id: 95128,
    original_filename: "AccessorialTerms.pdf",
    content_type: "application/pdf",
    file_size_bytes: 312004,
    page_count: null,
    status_id: 301,
    processed_at: null,
    is_deleted: false,
    created_on: "2026-05-20T09:15:33.512000",
    created_by: "Jamie Chen",
    extractions: [],
  },
  {
    id: 2064,
    s3_bucket: "dylt-apex-documents-qa-documents",
    s3_key: "default/2064/latest/FuelSurchargeAddendum.pdf",
    s3_version_id: null,
    document_type: "CARRIER_ADDENDUM",
    document_category: "linehaul",
    source: "web_portal",
    shipment_id: null,
    pro_number: null,
    trip_number: null,
    route_id: null,
    route_stop_id: null,
    carrier_id: 95128,
    original_filename: "FuelSurchargeAddendum.pdf",
    content_type: "application/pdf",
    file_size_bytes: 95612,
    page_count: null,
    status_id: 301,
    processed_at: null,
    is_deleted: false,
    created_on: "2026-05-18T16:48:02.100000",
    created_by: "Alex Kim",
    extractions: [],
  },
  {
    id: 2065,
    s3_bucket: "dylt-apex-documents-qa-documents",
    s3_key: "default/2065/latest/DetentionPolicy.pdf",
    s3_version_id: null,
    document_type: "CARRIER_ADDENDUM",
    document_category: "linehaul",
    source: "web_portal",
    shipment_id: null,
    pro_number: null,
    trip_number: null,
    route_id: null,
    route_stop_id: null,
    carrier_id: 95128,
    original_filename: "DetentionPolicy.pdf",
    content_type: "application/pdf",
    file_size_bytes: 145890,
    page_count: null,
    status_id: 301,
    processed_at: null,
    is_deleted: false,
    created_on: "2026-05-15T11:03:55.887000",
    created_by: "Tom Brown",
    extractions: [],
  },
];

export const mockAccessorialRates = {
  default: {
    carrierId: 95128,
    carrierCode: "L004",
    carrierName: "COVENANT TRANSPORT INC",
    isActive: true,
    stopOff: {
      id: 123,
      amount: 67,
      effectiveDateUtc: "2026-05-19T00:00:00",
      expiredDateUtc: null,
      createdBy: "5864ddf3-66e0-4cb7-9fdf-aa4828d6abba",
      createdOn: "2026-05-19T17:33:01.529106+00:00",
      modifiedBy: "5864ddf3-66e0-4cb7-9fdf-aa4828d6abba",
      modifiedOn: "2026-05-19T17:33:01.529106+00:00",
      isUpcomingExpiration: false,
    },
    rollover: {
      id: 119,
      amount: 125.75,
      effectiveDateUtc: "2026-05-18T00:00:00",
      expiredDateUtc: "2026-05-30T00:00:00",
      createdBy: null,
      createdOn: "2026-05-19T11:42:34.50416+00:00",
      modifiedBy: null,
      modifiedOn: "2026-05-19T11:42:34.50416+00:00",
      isUpcomingExpiration: false,
    },
    tonu: {
      id: 116,
      amount: 175.5,
      tonuCancellationWindow: 24,
      effectiveDateUtc: "2026-05-18T00:00:00",
      expiredDateUtc: null,
      createdBy: null,
      createdOn: "2026-05-19T11:45:32.017616+00:00",
      modifiedBy: null,
      modifiedOn: "2026-05-19T11:45:32.017616+00:00",
      isUpcomingExpiration: false,
    },
    detention: {
      id: 86,
      detentionFifteenMinuteIncrement: 18.75,
      detentionMaxTime: 250.5,
      detentionFreeTimeHours: 2,
      effectiveDateUtc: "2026-05-18T00:00:00",
      expiredDateUtc: null,
      createdBy: null,
      createdOn: "2026-05-19T11:47:14.830384+00:00",
      modifiedBy: null,
      modifiedOn: "2026-05-19T11:47:14.830384+00:00",
      isUpcomingExpiration: false,
    },
  },
} satisfies Record<string, LCodeAccessorialDetail>;

const mockAccessorialRatesDefault = mockAccessorialRates.default;

export const mockLinehaulLCodesCarrierDetail = {
  default: {
    carrier: {
      id: mockAccessorialRatesDefault.carrierId,
      name: mockAccessorialRatesDefault.carrierName,
      code: mockAccessorialRatesDefault.carrierCode,
    },
    pricingContact: {
      id: 1,
      firstName: "Mark",
      lastName: "Twain",
      email: "carrier@gmail.com",
      role: "(888) 123-4567",
    },
    accessorialRates: mockAccessorialRatesDefault,
    addendums: [],
    originSics: [
      { id: 1, code: "ATL", laneCount: 30, isExpiring: true },
      { id: 2, code: "BOS", laneCount: 30 },
      { id: 3, code: "CHT", laneCount: 30 },
      { id: 4, code: "LAX", laneCount: 30 },
      { id: 5, code: "EWR", laneCount: 30 },
      { id: 6, code: "HTX", laneCount: 30, isExpiring: true },
      { id: 7, code: "ATB-SEFL", laneCount: 30 },
      { id: 8, code: "SFO", laneCount: 30 },
      { id: 9, code: "ONT", laneCount: 30 },
      { id: 10, code: "WSH-PITD", laneCount: 30 },
      { id: 11, code: "IND-DAFG", laneCount: 30, isExpiring: true },
      { id: 12, code: "ATM", laneCount: 30 },
      { id: 13, code: "HTX", laneCount: 30, isExpiring: true },
      { id: 14, code: "BOS", laneCount: 30 },
      { id: 15, code: "ONT", laneCount: 30, isExpiring: true },
      { id: 16, code: "ATL", laneCount: 30 },
      { id: 17, code: "BOS", laneCount: 30 },
      { id: 18, code: "ONT", laneCount: 30 },
    ],
    originSicTotalCount: 18,
  },
} satisfies Record<string, LinehaulLCodesCarrierDetailContent>;

export const mockCarrierLanesResponse = {
  default: {
    carrierId: 95128,
    carrierCode: "L004",
    carrierName: "COVENANT TRANSPORT INC",
    originSics: mockLinehaulLCodesCarrierDetail.default.originSics.map(
      (sic) => ({
        sicId: sic.id,
        sicCode: sic.code,
        laneCount: sic.laneCount,
        isUpcomingRateExpiration: sic.isExpiring ?? false,
        destinations: [],
      })
    ),
  },
} satisfies Record<string, CarrierLanesResponse>;

/** Shared destination lanes shown under every origin SIC in the mocked destination list. */
const mockLCodeDestinationLanes = [
  {
    laneId: 54,
    sicId: 2,
    sicCode: "LAX",
    rates: [
      {
        rateId: 1,
        serviceType: 1,
        serviceTypeName: "Dedicated",
        amount: 2.15,
        effectiveDateUtc: "2026-04-01T00:00:00",
        expiredDateUtc: null,
        isActive: false,
        isUpcoming: true,
        isUpcomingRateExpiration: false,
        expiredBy: null,
        createdBy: "Jamie Chen",
        createdOn: "2026-02-02T10:00:00Z",
        modifiedBy: "Ravi Patel",
        modifiedOn: "2026-02-09T14:00:00Z",
      },
      {
        rateId: 2,
        serviceType: 2,
        serviceTypeName: "1-way",
        amount: 1.95,
        effectiveDateUtc: "2026-03-01T00:00:00",
        expiredDateUtc: "2026-05-24T00:00:00",
        isActive: true,
        isUpcoming: false,
        isUpcomingRateExpiration: true,
        expiredBy: null,
        createdBy: "Sophia Lee",
        createdOn: "2026-03-02T09:15:00Z",
        modifiedBy: "Jamie Chen",
        modifiedOn: "2026-03-05T16:20:00Z",
      },
      {
        rateId: 3,
        serviceType: 2,
        serviceTypeName: "1-way",
        amount: 2.95,
        effectiveDateUtc: "2026-06-01T00:00:00",
        expiredDateUtc: "2026-08-15T00:00:00",
        isActive: false,
        isUpcoming: true,
        isUpcomingRateExpiration: false,
        expiredBy: null,
        createdBy: "Sophia Lee",
        createdOn: "2026-03-02T09:15:00Z",
        modifiedBy: "Jamie Chen",
        modifiedOn: "2026-03-05T16:20:00Z",
      },
    ],
  },
  {
    laneId: 55,
    sicId: 11,
    sicCode: "IND-DAFG",
    rates: null,
  },
  {
    laneId: 56,
    sicId: 9,
    sicCode: "ONT",
    rates: [
      {
        rateId: 5,
        serviceType: 1,
        serviceTypeName: "Dedicated",
        amount: 2.05,
        effectiveDateUtc: "2026-02-15T00:00:00",
        expiredDateUtc: "2027-01-31T00:00:00",
        isActive: true,
        isUpcoming: false,
        isUpcomingRateExpiration: true,
        expiredBy: null,
        createdBy: "Chris Wong",
        createdOn: "2026-02-16T11:00:00Z",
        modifiedBy: "Chris Wong",
        modifiedOn: "2026-02-20T13:45:00Z",
      },
      {
        rateId: 6,
        serviceType: 3,
        serviceTypeName: "Shuttle",
        amount: 1.88,
        effectiveDateUtc: "2026-04-01T00:00:00",
        expiredDateUtc: "2027-03-31T00:00:00",
        isActive: false,
        isUpcoming: true,
        isUpcomingRateExpiration: false,
        expiredBy: null,
        createdBy: "Sophia Lee",
        createdOn: "2026-04-02T08:30:00Z",
        modifiedBy: "Jamie Chen",
        modifiedOn: "2026-04-05T12:10:00Z",
      },
    ],
  },
  {
    laneId: 57,
    sicId: 6,
    sicCode: "HTX",
    rates: [
      {
        rateId: 7,
        serviceType: 2,
        serviceTypeName: "1-way",
        amount: 2.22,
        effectiveDateUtc: "2026-05-01T00:00:00",
        expiredDateUtc: "2027-04-30T00:00:00",
        isActive: true,
        isUpcoming: false,
        isUpcomingRateExpiration: true,
        expiredBy: null,
        createdBy: "Ravi Patel",
        createdOn: "2026-05-02T10:20:00Z",
        modifiedBy: "Ravi Patel",
        modifiedOn: "2026-05-03T11:00:00Z",
      },
      {
        rateId: 8,
        serviceType: 1,
        serviceTypeName: "Dedicated",
        amount: 2.48,
        effectiveDateUtc: "2026-06-01T00:00:00",
        expiredDateUtc: "2026-12-31T00:00:00",
        isActive: false,
        isUpcoming: true,
        isUpcomingRateExpiration: true,
        expiredBy: null,
        createdBy: "Chris Wong",
        createdOn: "2026-02-03T09:00:00Z",
        modifiedBy: "Sophia Lee",
        modifiedOn: "2026-02-01T17:30:00Z",
      },
    ],
  },
  {
    laneId: 58,
    sicId: 1,
    sicCode: "ATL",
    rates: null,
  },
  {
    laneId: 59,
    sicId: 3,
    sicCode: "CHT",
    rates: null,
  },
] satisfies LCodeDestinationLane[];

const mockLinehaulLCodesCarrierDetailDefault =
  mockLinehaulLCodesCarrierDetail.default;

export const mockLCodeCarrierDestinations = {
  default: {
    carrierId: mockAccessorialRatesDefault.carrierId,
    carrierCode: mockAccessorialRatesDefault.carrierCode,
    carrierName: mockAccessorialRatesDefault.carrierName,
    originSics: mockLinehaulLCodesCarrierDetailDefault.originSics.map(
      (origin) => ({
        sicId: origin.id,
        sicCode: origin.code,
        laneCount: mockLCodeDestinationLanes.length,
        isUpcomingRateExpiration: Boolean(origin.isExpiring),
        destinations: mockLCodeDestinationLanes,
      })
    ),
  },
} satisfies Record<string, LCodeCarrierDestinations>;

export const mockExpiredAccessorialHistory: ExpiredAccessorialHistoryType[] = [
  {
    id: 1,
    amount: "$100",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
  {
    id: 2,
    amount: "$120",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
  {
    id: 3,
    amount: "$250",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
  {
    id: 4,
    amount: "$250",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
];

export const mockAddendumDocumentPreview = {
  default: {
    url: "https://www.rd.usda.gov/sites/default/files/pdf-sample_0.pdf",
    name: "pdf-sample_0.pdf",
    size: 100,
    type: "application/pdf",
    lastModified: "2026-01-23T14:00:00Z",
  },
} as const satisfies { default: MockAddendumDocumentPreview };

export const mockAddendumsList = {
  default: {
    documents: mockCarrierAddendums,
    total: mockCarrierAddendums.length,
    limit: 100,
    offset: 0,
    total_count: mockCarrierAddendums.length,
    page: 1,
    page_size: 100,
    total_pages: 1,
  },
} satisfies { default: CarrierAddendumsListResponse };

export const mockExpiredRatesLCodesHistory: ExpiredAccessorialHistoryType[] = [
  {
    id: 1,
    amount: "$100",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
  {
    id: 2,
    amount: "$120",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
  {
    id: 3,
    amount: "$250",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
  {
    id: 4,
    amount: "$250",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
];

export const mockExpiredRatesMCodesHistory: ExpiredAccessorialHistoryType[] = [
  {
    id: 1,
    amount: "$100",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
  {
    id: 2,
    amount: "$120",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
  {
    id: 3,
    amount: "$250",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
  {
    id: 4,
    amount: "$250",
    effectiveDate: "2026-01-01",
    expirationDate: "2026-01-10",
    expiredBy: "Tom Brown",
  },
];

export const mockExpiredTollsLaneHistory: ExpiredAccessorialHistoryType[] = [
  {
    id: 1,
    amount: "$100",
    effectiveDate: "2025-06-01",
    expirationDate: "2025-12-31",
    expiredBy: "Alex Kim",
  },
  {
    id: 2,
    amount: "$100",
    effectiveDate: "2024-01-15",
    expirationDate: "2025-05-30",
    expiredBy: "Jamie Chen",
  },
  {
    id: 3,
    amount: "$100",
    effectiveDate: "2024-01-15",
    expirationDate: "2025-05-30",
    expiredBy: "Jamie Chen",
  },
  {
    id: 4,
    amount: "$100",
    effectiveDate: "2024-01-15",
    expirationDate: "2025-05-30",
    expiredBy: "Jamie Chen",
  },
];
